import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { trackEvent } from "../lib/analytics";
import { MessageSquareText, ShieldCheck, Lock, ArrowRight, MoveRight } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1613611588152-9f6f858913fc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHw0fHx0aG91Z2h0ZnVsJTIwbWFufGVufDB8fHx8MTc4OTAwNzAwMHww&ixlib=rb-4.1.0&q=85";
const LISTEN_IMG =
  "https://images.unsplash.com/photo-1573497620166-aef748c8c792?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwxfHx3b21hbiUyMGxpc3RlbmluZ3xlbnwwfHx8fDE3ODkwMDcwMDB8MA&ixlib=rb-4.1.0&q=85";
const CONV_IMG =
  "https://images.unsplash.com/photo-1544486927-c903fcc7dcc3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwzfHx0d28lMjBtZW4lMjB0YWxraW5nfGVufDB8fHx8MTc4OTAwNzAwMHww&ixlib=rb-4.1.0&q=85";

const INK = "#0E1417";

function LandingNav({ user }) {
  return (
    <header className="absolute top-0 inset-x-0 z-30">
      <div className="max-w-6xl mx-auto flex items-center justify-between px-6 py-5">
        <Link to={user ? "/chat" : "/"} data-testid="nav-logo" className="flex items-center gap-2.5 group">
          <div className="w-9 h-9 rounded-xl bg-[#2E9D96] flex items-center justify-center text-white shadow-[0_4px_14px_rgba(46,157,150,0.4)]">
            <MessageSquareText size={17} />
          </div>
          <span className="font-grotesk font-bold text-[22px] tracking-tight text-white">m8 im here</span>
        </Link>
        <nav className="flex items-center gap-3 sm:gap-5">
          <Link
            to="/login"
            data-testid="nav-login"
            className="text-sm font-semibold text-slate-200 hover:text-white transition-colors px-2"
          >
            Log in
          </Link>
          <Link
            to="/signup"
            data-testid="nav-join"
            className="text-sm font-semibold text-white bg-white/10 hover:bg-white/20 border border-white/15 rounded-full px-5 py-2 backdrop-blur-sm transition-colors"
          >
            Join
          </Link>
        </nav>
      </div>
    </header>
  );
}

export default function Landing() {
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-[#F6F7F6]">
      {/* ================= HERO (dark, grounded) ================= */}
      <section className="relative overflow-hidden grain-dark" style={{ backgroundColor: INK }}>
        {/* soft steel-teal glow */}
        <div
          className="pointer-events-none absolute -top-40 -right-32 w-[620px] h-[620px] rounded-full opacity-[0.22] blur-[120px]"
          style={{ background: "radial-gradient(circle, #2E9D96 0%, transparent 70%)" }}
        />
        <div
          className="pointer-events-none absolute bottom-0 -left-40 w-[520px] h-[520px] rounded-full opacity-[0.14] blur-[130px]"
          style={{ background: "radial-gradient(circle, #3F6E8C 0%, transparent 70%)" }}
        />

        <LandingNav user={user} />

        <div className="relative max-w-6xl mx-auto px-6 pt-32 pb-24 md:pt-40 md:pb-28 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-7">
            <div className="rise-in d-1 inline-flex items-center gap-2 rounded-full border border-white/12 bg-white/[0.04] px-4 py-1.5 mb-8">
              <span className="w-1.5 h-1.5 rounded-full bg-[#2E9D96]" />
              <span className="text-[11px] uppercase tracking-[0.28em] text-slate-300 font-semibold">
                You're never truly alone
              </span>
            </div>

            <h1 className="rise-in d-2 font-grotesk font-bold text-4xl sm:text-5xl lg:text-[64px] leading-[1.02] text-white">
              Someone checks in on you.
              <br />
              <span className="text-[#3FC1B7]">Every single day.</span>
            </h1>

            <p className="rise-in d-3 mt-7 text-lg text-slate-300 leading-relaxed max-w-xl">
              Zaf asks how you're doing every day — and if you ever go quiet, someone you trust gets a
              gentle nudge. Always in your corner, day or night, so the heavy stuff never has to be
              carried alone.
            </p>

            <p className="rise-in d-3 mt-3 text-sm text-slate-400 leading-relaxed max-w-xl">
              Zaf is a private companion built just for you — powered by AI, here whenever you need it.
            </p>

            <div className="rise-in d-4 mt-9 flex flex-wrap items-center gap-4">
              <Link
                data-testid="hero-join-btn"
                to="/try"
                onClick={() => trackEvent("TryZafClicked")}
                className="group inline-flex items-center gap-2 bg-[#2E9D96] hover:bg-[#38B3AB] text-white font-semibold px-7 py-3.5 rounded-full transition-all hover:-translate-y-0.5 shadow-[0_10px_30px_rgba(46,157,150,0.35)]"
              >
                Start talking — free, no signup
                <ArrowRight size={17} className="transition-transform group-hover:translate-x-0.5" />
              </Link>
              <Link
                data-testid="hero-login-btn"
                to="/login"
                className="text-slate-200 hover:text-white font-semibold transition-colors"
              >
                I already have an account
              </Link>
            </div>

            <p className="rise-in d-5 mt-8 text-sm text-slate-400">
              Then just <span className="text-slate-200 font-semibold">$6.49 / week</span> — about a coffee.
              No lock-in. Walk away anytime.
            </p>
          </div>

          {/* Hero image + quiet message */}
          <div className="md:col-span-5 relative rise-in d-3">
            <div className="relative rounded-[1.75rem] overflow-hidden border border-white/10 shadow-2xl">
              <img src={HERO_IMG} alt="A man taking a quiet moment by the window" className="w-full h-[440px] object-cover" />
              <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(14,20,23,0.55), transparent 55%)" }} />
            </div>
            <div className="absolute -bottom-6 -left-4 sm:-left-6 bg-[#151E22] rounded-2xl shadow-xl px-5 py-4 border border-white/10 max-w-[250px]">
              <p className="text-[10px] uppercase tracking-[0.22em] text-[#3FC1B7] font-semibold">Zaf · 1:47am</p>
              <p className="mt-1.5 text-[15px] text-slate-200 leading-snug">
                Still up? No rush. I'm here whenever you feel like talking.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ================= "Say it here" band ================= */}
      <section className="relative bg-[#F6F7F6]">
        <div className="max-w-5xl mx-auto px-6 py-20 md:py-24 text-center">
          <p className="text-xs uppercase tracking-[0.26em] text-[#2E9D96] font-semibold mb-5">No one has to know</p>
          <h2 className="font-grotesk font-semibold text-2xl sm:text-3xl lg:text-4xl text-slate-900 leading-snug max-w-3xl mx-auto">
            You don't have to have the words. You just have to start typing.
          </h2>
          <p className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto">
            Some things are easier to type than to say out loud. Zaf doesn't need the full story — a rough
            day, a weird mood, a 2am spiral. Whatever it is, it can go here first.
          </p>
        </div>
      </section>

      {/* ================= Value props ================= */}
      <section className="max-w-6xl mx-auto px-6 pb-20 grid md:grid-cols-3 gap-5">
        {[
          {
            icon: <ShieldCheck size={22} />,
            title: "Someone knows if you go quiet",
            body: "Name one person you trust. Miss a few check-ins and we quietly reach out to them. A safety net that never sleeps.",
          },
          {
            icon: <MessageSquareText size={22} />,
            title: "A daily 'you good?'",
            body: "Zaf checks in every day and actually remembers who you are — picking up right where you left off, like a mate would.",
          },
          {
            icon: <Lock size={22} />,
            title: "Yours, and only yours",
            body: "No members messaging members. No feed. What you share with Zaf stays between you and Zaf.",
          },
        ].map((f, i) => (
          <div
            key={i}
            className="bg-white rounded-3xl p-8 border border-slate-200/70 shadow-sm hover:-translate-y-1 hover:shadow-md transition-all duration-300"
          >
            <div className="w-12 h-12 rounded-2xl bg-[#0E1417] text-[#3FC1B7] flex items-center justify-center mb-6">
              {f.icon}
            </div>
            <h3 className="font-grotesk font-semibold text-xl text-slate-900 mb-2.5">{f.title}</h3>
            <p className="text-slate-600 leading-relaxed text-[15px]">{f.body}</p>
          </div>
        ))}
      </section>

      {/* ================= Promise block (mixed imagery) ================= */}
      <section className="max-w-6xl mx-auto px-6 py-8 md:py-16 grid md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-5">
          <div className="grid grid-cols-2 gap-4">
            <div className="rounded-3xl overflow-hidden shadow-md mt-8">
              <img src={LISTEN_IMG} alt="Someone listening with warmth" className="w-full h-[240px] object-cover" />
            </div>
            <div className="rounded-3xl overflow-hidden shadow-md">
              <img src={CONV_IMG} alt="Two people in an honest conversation" className="w-full h-[240px] object-cover" />
            </div>
          </div>
        </div>
        <div className="md:col-span-7">
          <p className="text-xs uppercase tracking-[0.26em] text-[#2E9D96] font-semibold mb-5">Zaf's promise</p>
          <blockquote className="font-grotesk font-medium text-2xl md:text-[34px] leading-[1.25] text-slate-900">
            "When it's heavy, I'll help you carry it. When it's good, I'm your biggest fan.
            And if it ever gets too much — I'll help you get real help, fast."
          </blockquote>
          <div className="mt-9 grid sm:grid-cols-2 gap-6 max-w-lg">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-xl bg-[#E3F0EE] text-[#1F7B75] flex items-center justify-center shrink-0">
                <MessageSquareText size={16} />
              </div>
              <div>
                <p className="font-semibold text-slate-900 text-[15px]">A daily check-in</p>
                <p className="text-slate-500 text-sm leading-snug">A quiet "you good?" — every single day.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-xl bg-[#E3F0EE] text-[#1F7B75] flex items-center justify-center shrink-0">
                <ShieldCheck size={16} />
              </div>
              <div>
                <p className="font-semibold text-slate-900 text-[15px]">A safety net</p>
                <p className="text-slate-500 text-sm leading-snug">Your trusted contact gets the nudge if you can't.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= How it works ================= */}
      <section className="bg-[#0E1417] grain-dark relative overflow-hidden">
        <div
          className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[400px] rounded-full opacity-[0.12] blur-[130px]"
          style={{ background: "radial-gradient(circle, #2E9D96 0%, transparent 70%)" }}
        />
        <div className="relative max-w-6xl mx-auto px-6 py-20 md:py-24">
          <p className="text-xs uppercase tracking-[0.26em] text-[#3FC1B7] font-semibold mb-4 text-center">Dead simple</p>
          <h2 className="font-grotesk font-semibold text-2xl sm:text-3xl lg:text-4xl text-white text-center mb-14">
            Three steps. Then just talk.
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { n: "01", t: "Sign up in a minute", d: "Email and a password. That's it. No forms about your feelings before you've even started." },
              { n: "02", t: "Name one person you trust", d: "The one we'd reach out to if you ever went quiet. Your safety net, set once." },
              { n: "03", t: "Say whatever's on your mind", d: "Type it, or share a photo of your day. Zaf's there — 1am on a Tuesday included." },
            ].map((s, i) => (
              <div key={i} className="rounded-3xl border border-white/10 bg-white/[0.03] p-8">
                <span className="font-grotesk font-bold text-3xl text-[#3FC1B7]">{s.n}</span>
                <h3 className="font-grotesk font-semibold text-lg text-white mt-4 mb-2">{s.t}</h3>
                <p className="text-slate-400 leading-relaxed text-[15px]">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= Pricing ================= */}
      <section className="max-w-4xl mx-auto px-6 py-20 md:py-24">
        <div className="bg-white rounded-[2rem] p-10 md:p-14 border border-slate-200/70 shadow-sm text-center">
          <p className="text-xs uppercase tracking-[0.26em] text-[#2E9D96] font-semibold mb-4">One simple price</p>
          <h2 className="font-grotesk font-semibold text-3xl md:text-[42px] text-slate-900 tracking-tight">
            Less than a coffee. All week.
          </h2>
          <div className="mt-8 flex items-baseline justify-center gap-2">
            <span className="font-grotesk font-bold text-6xl text-slate-900">$6.49</span>
            <span className="text-slate-500 font-semibold">USD / week</span>
          </div>
          <p className="mt-3 text-slate-500">First 3 days free. No lock-in. Quit anytime — no awkward conversation.</p>
          <Link
            data-testid="pricing-join-btn"
            to="/signup"
            className="group inline-flex mt-9 items-center gap-2 bg-[#2E9D96] hover:bg-[#38B3AB] text-white font-semibold px-8 py-3.5 rounded-full transition-all hover:-translate-y-0.5 shadow-[0_10px_30px_rgba(46,157,150,0.3)]"
          >
            Try it free for 3 days
            <MoveRight size={17} className="transition-transform group-hover:translate-x-0.5" />
          </Link>
        </div>
      </section>

      <footer className="border-t border-slate-200/70">
        <div className="max-w-6xl mx-auto px-6 py-10 text-sm text-slate-500 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg bg-[#0E1417] flex items-center justify-center text-[#3FC1B7]">
              <MessageSquareText size={13} />
            </div>
            <span className="font-grotesk font-semibold text-slate-700">m8 im here</span>
          </div>
          <p>© {new Date().getFullYear()} m8 im here</p>
          <div className="flex items-center gap-6">
            <Link to="/impact" className="hover:text-slate-700 transition-colors" data-testid="footer-impact">For partners</Link>
            <span className="hover:text-slate-700 transition-colors cursor-pointer">Privacy</span>
            <span className="hover:text-slate-700 transition-colors cursor-pointer">Terms</span>
            <a href="mailto:questions@m8imhere.com" className="hover:text-slate-700 transition-colors" data-testid="footer-contact">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
